import type {
  ChatErrorPayload,
  ChatJsonResponse,
  ChatUsage,
  ConversationDetail,
  ConversationPage,
  FeedbackReason,
  MessageFeedback,
  SavedDocumentRef,
  TurnMeta,
  TurnRequest,
} from './types';

export class ChatError extends Error {
  requestId: string | null;
  /** The HTTP status, when the failure was a response rather than a network error. */
  status: number | null;
  /** The conversation the failed turn belongs to, when the server said. */
  conversationId: string | null;

  constructor(
    message: string,
    requestId: string | null = null,
    status: number | null = null,
    conversationId: string | null = null,
  ) {
    super(message);
    this.name = 'ChatError';
    this.requestId = requestId;
    this.status = status;
    this.conversationId = conversationId;
  }
}

/**
 * The Go API serialises genkit's GenerationUsage in camelCase (`inputTokens`),
 * while the widget uses snake_case. Accept either and default missing counts to 0.
 */
function normalizeUsage(raw: unknown): ChatUsage | null {
  if (!raw || typeof raw !== 'object') return null;
  const u = raw as Record<string, unknown>;
  const num = (...keys: string[]) => {
    for (const k of keys) if (typeof u[k] === 'number') return u[k] as number;
    return 0;
  };
  return {
    input_tokens: num('input_tokens', 'inputTokens'),
    output_tokens: num('output_tokens', 'outputTokens'),
    cached_content_token_count: num('cached_content_token_count', 'cachedContentTokens'),
  };
}

/** The {id, document_no, url} of a saved document, or null when the payload has none. */
function normalizeDocument(raw: unknown): SavedDocumentRef | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  return {
    ...(typeof o.id === 'number' ? { id: o.id } : {}),
    ...(typeof o.document_no === 'string' ? { document_no: o.document_no } : {}),
    ...(typeof o.url === 'string' ? { url: o.url } : {}),
  };
}

export interface StreamHandlers {
  /**
   * The conversation this turn belongs to — sent first, so a turn that then
   * fails still leaves the browser knowing where its message was stored.
   */
  onConversation: (conversationId: string) => void;
  onDelta: (text: string) => void;
  onDone: (meta: TurnMeta) => void;
  /**
   * The model asked for a tool. Optional: a host that only wants the text can
   * leave these out and the tool events are ignored, exactly as they were
   * before the API started sending them.
   */
  onToolStart?: (call: StepEvent) => void;
  /** That tool finished; `ref` matches the one from onToolStart. */
  onToolEnd?: (end: StepEnd) => void;
  /**
   * An agent started — the one running the whole turn, or a sub-agent it
   * handed work to. Whatever starts while it is open ran inside it.
   */
  onAgentStart?: (call: StepEvent) => void;
  /** That agent finished; `ref` matches the one from onAgentStart. */
  onAgentEnd?: (end: StepEnd) => void;
}

/** A tool or sub-agent starting, with the API's wording for it if it sent one. */
export interface StepEvent {
  ref: string;
  name: string;
  label?: string;
}

export interface StepEnd {
  ref: string;
  label?: string;
}

function stepLabel(payload: Record<string, unknown>): { label?: string } {
  return typeof payload.label === 'string' && payload.label !== '' ? { label: payload.label } : {};
}

interface ParsedEvent {
  name: string;
  data: string;
}

/**
 * Split raw SSE bytes into complete events.
 *
 * A chunk boundary has no relationship to an event boundary — one event can
 * arrive across three reads, and three events can arrive in one — so whatever
 * is incomplete stays in the buffer for the next call.
 */
function createSseSplitter() {
  let buffer = '';

  return (chunk: string): ParsedEvent[] => {
    buffer = (buffer + chunk).replace(/\r\n/g, '\n');

    const events: ParsedEvent[] = [];
    let split = buffer.indexOf('\n\n');

    while (split !== -1) {
      const block = buffer.slice(0, split);
      buffer = buffer.slice(split + 2);

      let name = 'message';
      const data: string[] = [];

      for (const line of block.split('\n')) {
        // Blank line, or a comment/heartbeat like ": keep-alive".
        if (line === '' || line.startsWith(':')) continue;

        const colon = line.indexOf(':');
        if (colon === -1) continue;

        const field = line.slice(0, colon);
        const value = line.slice(colon + 1).replace(/^ /, '');

        if (field === 'event') name = value;
        else if (field === 'data') data.push(value);
      }

      if (data.length > 0) events.push({ name, data: data.join('\n') });

      split = buffer.indexOf('\n\n');
    }

    return events;
  };
}

/**
 * The id that pairs a tool_end with its tool_start. Providers that don't issue
 * call ids leave `ref` empty, in which case the tool name is the best key
 * available — good enough unless the same tool is called twice in one turn.
 */
function toolRef(payload: Record<string, unknown>, name: string): string {
  return typeof payload.ref === 'string' && payload.ref !== '' ? payload.ref : name;
}

function parseJson(data: string): Record<string, unknown> {
  try {
    const parsed = JSON.parse(data);
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : {};
  } catch {
    return {};
  }
}

/**
 * Stream one assistant turn.
 *
 * Uses fetch + a body reader rather than EventSource, which is GET-only and
 * cannot carry the CSRF header or a JSON body.
 */
export async function streamChat(
  chatUrl: string,
  csrfToken: string,
  turn: TurnRequest,
  handlers: StreamHandlers,
  signal: AbortSignal,
): Promise<void> {
  let res: Response;

  try {
    res = await fetch(chatUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-TOKEN': csrfToken,
        Accept: 'text/event-stream',
      },
      body: JSON.stringify(turn),
      credentials: 'same-origin',
      signal,
    });
  } catch (e: unknown) {
    if (signal.aborted) return;
    throw new ChatError(
      'Could not reach the assistant. ' + (e instanceof Error ? e.message : ''),
    );
  }

  // A validation or auth failure arrives as JSON with a real status, because
  // it happens before the first streamed byte.
  if (!res.ok) {
    let payload: ChatErrorPayload | null = null;
    try {
      payload = (await res.json()) as ChatErrorPayload;
    } catch {
      /* fall through to the generic message */
    }
    throw new ChatError(
      payload?.error || `The assistant request failed (HTTP ${res.status}).`,
      payload?.request_id ?? null,
      res.status,
      payload?.conversation_id ?? null,
    );
  }

  if (!res.body) {
    throw new ChatError('This browser cannot read a streamed response.');
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  const split = createSseSplitter();

  let messageId: number | null = null;
  let requestId: string | null = null;
  let usage: ChatUsage | null = null;
  let truncated = false;
  let completed = false;
  let document: SavedDocumentRef | null = null;

  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;

      const chunk = decoder.decode(value, { stream: true });

      for (const event of split(chunk)) {
        const payload = parseJson(event.data);

        if (typeof payload.request_id === 'string') requestId = payload.request_id;

        switch (event.name) {
          case 'conversation':
            if (typeof payload.conversation_id === 'string') {
              handlers.onConversation(payload.conversation_id);
            }
            break;

          case 'delta':
          case 'chunk':
          case 'message':
            if (typeof payload.text === 'string') handlers.onDelta(payload.text);
            break;

          case 'tool_start': {
            const name = typeof payload.name === 'string' ? payload.name : '';
            if (name === '') break;
            handlers.onToolStart?.({ ref: toolRef(payload, name), name, ...stepLabel(payload) });
            break;
          }

          case 'tool_end': {
            const name = typeof payload.name === 'string' ? payload.name : '';
            handlers.onToolEnd?.({ ref: toolRef(payload, name), ...stepLabel(payload) });
            break;
          }

          // tool_progress carries a tool's own mid-run update. Nothing streams
          // one today, and the chip is already showing, so it is ignored.
          case 'tool_progress':
            break;

          case 'agent_start': {
            const name = typeof payload.name === 'string' ? payload.name : '';
            if (name === '') break;
            handlers.onAgentStart?.({ ref: toolRef(payload, name), name, ...stepLabel(payload) });
            break;
          }

          case 'agent_end': {
            const name = typeof payload.name === 'string' ? payload.name : '';
            handlers.onAgentEnd?.({ ref: toolRef(payload, name), ...stepLabel(payload) });
            break;
          }

          case 'done':
            usage = normalizeUsage(payload.usage) ?? usage;
            truncated = payload.finish_reason === 'length';
            completed = payload.completed === true;
            document = normalizeDocument(payload.document);
            break;

          case 'error':
            throw new ChatError(
              typeof payload.error === 'string' ? payload.error : 'The assistant failed mid-reply.',
              requestId,
            );

          // Sent by this app, not the API, once the reply is stored.
          case 'close':
            if (typeof payload.message_id === 'number') messageId = payload.message_id;
            break;
        }
      }
    }
  } finally {
    // Abort leaves the body half-read; releasing the lock lets the connection
    // be torn down instead of hanging until GC.
    reader.releaseLock();
  }

  handlers.onDone({ messageId, requestId, usage, truncated, completed, document });
}

/** Non-streaming path, used when `assistant.stream` is false. */
export async function sendChat(
  chatUrl: string,
  csrfToken: string,
  turn: TurnRequest,
  signal: AbortSignal,
): Promise<{ text: string; conversationId: string | null } & TurnMeta> {
  let res: Response;

  try {
    res = await fetch(chatUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-TOKEN': csrfToken,
        Accept: 'application/json',
      },
      body: JSON.stringify(turn),
      credentials: 'same-origin',
      signal,
    });
  } catch (e: unknown) {
    if (signal.aborted) {
      return {
        text: '',
        conversationId: null,
        messageId: null,
        requestId: null,
        usage: null,
        truncated: false,
        completed: false,
        document: null,
      };
    }
    throw new ChatError(
      'Could not reach the assistant. ' + (e instanceof Error ? e.message : ''),
    );
  }

  let body: unknown;
  try {
    body = await res.json();
  } catch {
    throw new ChatError(`The assistant returned a non-JSON response (HTTP ${res.status}).`);
  }

  if (!res.ok) {
    const err = body as ChatErrorPayload;
    throw new ChatError(
      err?.error || `The assistant request failed (HTTP ${res.status}).`,
      err?.request_id ?? null,
      res.status,
      err?.conversation_id ?? null,
    );
  }

  const ok = body as ChatJsonResponse;
  return {
    text: ok.data?.text ?? '',
    conversationId: ok.conversation_id ?? null,
    messageId: typeof ok.message_id === 'number' ? ok.message_id : null,
    requestId: ok.request_id ?? null,
    usage: normalizeUsage(ok.usages),
    truncated: Boolean(ok.data?.truncated),
    completed: ok.data?.completed === true,
    document: normalizeDocument(ok.data?.document),
  };
}

/* ------------------------------------------------------------------ *
 * History
 * ------------------------------------------------------------------ */

async function requestJson<T>(url: string, init: RequestInit = {}): Promise<T> {
  let res: Response;
  try {
    res = await fetch(url, {
      ...init,
      headers: { Accept: 'application/json', ...(init.headers ?? {}) },
      credentials: 'same-origin',
    });
  } catch (e: unknown) {
    throw new ChatError('Could not reach the assistant. ' + (e instanceof Error ? e.message : ''));
  }

  let body: unknown = null;
  try {
    body = await res.json();
  } catch {
    /* handled below */
  }

  if (!res.ok || body === null) {
    const err = body as ChatErrorPayload | null;
    throw new ChatError(
      err?.error || `The assistant request failed (HTTP ${res.status}).`,
      null,
      res.status,
    );
  }

  return body as T;
}

/** The URL one stored attachment is served from. */
export function attachmentUrl(
  conversationsUrl: string,
  conversationId: string,
  messageId: number,
  index: number,
): string {
  return `${conversationsUrl}/${encodeURIComponent(conversationId)}/attachments/${messageId}/${index}`;
}

export function listConversations(conversationsUrl: string, page: number): Promise<ConversationPage> {
  return requestJson<ConversationPage>(`${conversationsUrl}?page=${page}`);
}

export async function getConversation(
  conversationsUrl: string,
  conversationId: string,
): Promise<ConversationDetail> {
  const body = await requestJson<{ data: ConversationDetail }>(
    `${conversationsUrl}/${encodeURIComponent(conversationId)}`,
  );
  return body.data;
}

export async function deleteConversation(
  conversationsUrl: string,
  csrfToken: string,
  conversationId: string,
): Promise<void> {
  await requestJson(`${conversationsUrl}/${encodeURIComponent(conversationId)}`, {
    method: 'DELETE',
    headers: { 'X-CSRF-TOKEN': csrfToken },
  });
}

/**
 * Rate one stored reply; rating 0 takes the rating back. Resolves to the
 * rating as stored, or null once it is taken back.
 */
export async function sendFeedback(
  conversationsUrl: string,
  csrfToken: string,
  conversationId: string,
  messageId: number,
  rating: 1 | -1 | 0,
  reasons: FeedbackReason[] = [],
  comment: string | null = null,
): Promise<MessageFeedback | null> {
  const body = await requestJson<{ feedback: MessageFeedback | null }>(
    `${conversationsUrl}/${encodeURIComponent(conversationId)}/feedback`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': csrfToken },
      body: JSON.stringify({ message_id: messageId, rating, reasons, comment }),
    },
  );
  return body.feedback;
}
